/**
 * NavigationBackContext (FT-356)
 *
 * Shell-level context that lets any mounted view register a "back" handler.
 * The MobileBottomBar reads the currently-active handler (the TOP of the
 * back-handler stack) and renders a Back button on the LEFT of the bar
 * (natural mobile UX). When the stack is empty (Home / top-level modules
 * that didn't register a handler), the Back button is hidden.
 *
 * Design intent:
 *   - Views do NOT own the "back" widget. The bottom bar is the single
 *     rendering surface for back navigation on mobile.
 *   - Views register their handler on mount via `useSetBackHandler(handler)`.
 *     Registration is stack-based so nested drill-downs naturally "pop" back
 *     to the parent's handler when they unmount.
 *   - The hook returns nothing; callers pass their handler and forget.
 *
 * Stack semantics:
 *   - Each mounted view owns one stack entry (identified by a stable id).
 *   - The entry's handler is updated in place whenever the caller's
 *     `handler` prop changes — no push/pop churn on identity changes from
 *     inline arrow functions.
 *   - MobileBottomBar fires the topmost non-null handler.
 *   - On unmount, the entry is removed and the previous topmost entry (if
 *     any) becomes active again automatically.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BOTTOM BAR CONTEXTUAL ACTIONS PATTERN (FT-403)
 * ─────────────────────────────────────────────────────────────────────────
 *
 * NavigationBackContext owns BACK navigation only (the left slot of the bar).
 * For CONTEXTUAL ACTIONS on a detail/form page (e.g. "Sign Off", "Save",
 * "Submit") use the MIDDLE SLOT via MobileBottomBarActionsContext:
 *
 *   import { useSetBottomBarActions }
 *     from '../../../contexts/MobileBottomBarActionsContext';
 *
 *   // In your view component:
 *   useSetBottomBarActions(
 *     shouldShowAction
 *       ? [
 *           {
 *             key: 'unique-key',      // stable, used for React reconciliation
 *             icon: SomeLucideIcon,   // lucide-react icon component
 *             label: 'Sign Off',      // short label shown under the icon
 *             primary: true,          // fills the icon in a cyan-600 circle
 *             disabled: isWorking,    // greys out and prevents taps
 *             onClick: handleAction,  // the action callback
 *           },
 *         ]
 *       : []  // empty array = no middle slot actions (bar stays clean)
 *   );
 *
 * Rules for using this pattern:
 *
 *   1. MOBILE ONLY. The `<footer data-role="mobile-bottom-bar">` carries
 *      `lg:hidden` — it is invisible on desktop. Always keep the equivalent
 *      button in the page header for desktop, using `hidden lg:inline-flex`
 *      so it stays out of the way on mobile (where the bar owns it).
 *
 *   2. SAME RBAC GATE. The bottom bar button and the header button must be
 *      gated by the same `can(action, resource)` check. Never show a bottom
 *      bar action to a user who couldn't see the header button.
 *
 *   3. STABLE CALLBACKS. `useSetBottomBarActions` detects shape changes
 *      (label / primary / disabled flags) but intentionally ignores pure
 *      onClick identity churn (inline arrow functions on every render).
 *      Wrap your handler in `useCallback` to be explicit about dependencies.
 *
 *   4. STACK-BASED CLEANUP. The hook pushes on mount and pops on unmount —
 *      you never need to manually clear it. When a detail view unmounts, the
 *      parent's actions (if any) automatically reappear.
 *
 *   5. CONFIRMATION DIALOGS. If the action is irreversible (sign-off, delete,
 *      submit) add a local `showConfirm` boolean state and show a simple
 *      inline modal BEFORE calling the real callback. Both the bottom bar
 *      button and the header button should open the same dialog.
 *
 * Example screens that already use this pattern (FT-403):
 *   - MaintenanceCompletionReport.js  (Sign Off action)
 *   - DrillDetailView.js              (Sign Off action)
 * ─────────────────────────────────────────────────────────────────────────
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NOOP = () => {};

export const NavigationBackContext = createContext({
  onBack: null,
  pushBackEntry: () => 0,
  updateBackEntry: NOOP,
  removeBackEntry: NOOP,
});

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return idCounter;
};

/**
 * Provider — mount at the shell level, ABOVE any view that might want to
 * register a back handler (i.e. inside TailwindShell, wrapping <main>).
 */
export function NavigationBackProvider({ children }) {
  // Stack entries: { id, handler }. Topmost entry (last element) is active.
  const [stack, setStack] = useState([]);

  const pushBackEntry = useCallback((handler) => {
    const id = nextId();
    setStack((prev) => [
      ...prev,
      { id, handler: typeof handler === 'function' ? handler : null },
    ]);
    return id;
  }, []);

  const updateBackEntry = useCallback((id, handler) => {
    setStack((prev) => {
      const idx = prev.findIndex((entry) => entry.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = {
        id,
        handler: typeof handler === 'function' ? handler : null,
      };
      return next;
    });
  }, []);

  const removeBackEntry = useCallback((id) => {
    setStack((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // Active handler = topmost entry with a non-null handler. If the topmost
  // entry has null (view opted out), fall back to the next entry below —
  // keeps the Back button available when a nested view has no back of its
  // own but its parent does.
  const onBack = useMemo(() => {
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].handler) return stack[i].handler;
    }
    return null;
  }, [stack]);

  const value = useMemo(
    () => ({ onBack, pushBackEntry, updateBackEntry, removeBackEntry }),
    [onBack, pushBackEntry, updateBackEntry, removeBackEntry],
  );

  return (
    <NavigationBackContext.Provider value={value}>
      {children}
    </NavigationBackContext.Provider>
  );
}

/**
 * useSetBackHandler(handler)
 *
 * Register a back handler for this view. On mount the handler is pushed onto
 * the shell-level stack; on unmount it is popped. When the caller's
 * `handler` prop identity changes, the existing stack entry is updated in
 * place — no push/pop churn from inline arrow functions.
 *
 * Pass `null` / `undefined` to indicate "no back for me". The stack entry
 * still exists (so cleanup on unmount works cleanly) but is skipped by the
 * bar; whichever parent last registered a real handler remains active.
 */
export function useSetBackHandler(handler) {
  const { pushBackEntry, updateBackEntry, removeBackEntry } = useContext(
    NavigationBackContext,
  );

  // Track the id of our stack entry across renders.
  const idRef = useRef(null);

  // Keep a mutable ref always pointing to the latest handler.
  // Updated synchronously in render — safe for refs, triggers no re-render.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // A single stable wrapper that never changes identity across renders.
  // Delegates to handlerRef.current so pressing back always calls the
  // *current* handler, even if the parent re-rendered with a new reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableWrapper = useRef((...args) => handlerRef.current?.(...args));

  // Push on mount, pop on unmount. Uses stable wrapper — never churn.
  useEffect(() => {
    const id = pushBackEntry(handler == null ? null : stableWrapper.current);
    idRef.current = id;
    return () => {
      removeBackEntry(id);
      idRef.current = null;
    };
    // Push exactly once per view lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update context ONLY for null↔non-null transitions (back button appears/
  // disappears). Skipping fn→fn reference churn prevents setStack() firing on
  // every 5-second sync cycle → no MobileBottomBar re-renders → no iOS
  // touch-event drops across all 60+ useSetBackHandler call sites.
  const wasNullRef = useRef(handler == null);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const isNull = handler == null;
    const wasNull = wasNullRef.current;
    wasNullRef.current = isNull;
    if (wasNull !== isNull && idRef.current != null) {
      updateBackEntry(idRef.current, isNull ? null : stableWrapper.current);
    }
  }, [handler, updateBackEntry]);
}

/**
 * useBackHandler() — read-only accessor. Returns the currently-active back
 * handler (the topmost non-null entry on the stack) or null. Used by
 * MobileBottomBar to decide whether/how to render the Back button.
 */
export function useBackHandler() {
  return useContext(NavigationBackContext).onBack;
}
